/**
 * Entity Quality Thresholds - Standalone Configuration
 * 
 * Extracted to break circular dependency between:
 * - entitySourceConfig.ts (imports this)
 * - metadataGovernance.ts (re-exports this)
 * 
 * Quality thresholds for entity metadata validation.
 * 
 * SINGLE SOURCE OF TRUTH for entity-specific quality thresholds.
 * 
 * @sync BACKEND: supabase/functions/_shared/config/field-compliance-config.ts
 * @test src/config/__tests__/qualityConfigParity.test.ts
 * @see docs/QUALITY_GOVERNANCE.md
 */

import type { LocalizationContentType } from '@/constants/localization';

export interface QualityThresholds {
  // Entity name constraints (translated_primary) - entity names can be short
  minNameLength: number;
  maxNameLength: number;
  
  // Short name constraints (translated_primary_short) - taglines/breadcrumbs
  minShortNameLength?: number;
  maxShortNameLength?: number;
  
  // SEO title constraints (meta_title) - Google 2025 Best Practices
  minTitleLength: number;
  maxTitleLength: number;
  
  // Body/description constraints
  minBodyLength: number;
  maxDescLength: number;
  
  // Excerpt-specific thresholds (translated_body_short)
  // Excerpts are 50-700 chars, distinct from full body content
  minExcerptLength?: number;
  maxExcerptLength?: number;
}

/**
 * Entity-specific quality thresholds - Google 2025 Best Practices
 * 
 * IMPORTANT: Distinguishes between entity NAMES (translated_primary) and SEO TITLES (meta_title):
 * - minNameLength: Entity names like "US", "Tax", "Belize" are naturally short (2-10 chars)
 * - minTitleLength: SEO titles need substance (30+ chars) for SERP optimization
 * 
 * RATIONALE (Google 2025):
 * - profile maxTitleLength 70: Profile names like "John Smith - Tax Advisor - NYC" need extra chars
 * - faq maxTitleLength 120: FAQ questions should be concise for FAQ schema
 * - faq maxDescLength 300: FAQ answers can be longer but not excessive
 * 
 * SYNC REQUIRED: supabase/functions/_shared/config/field-compliance-config.ts
 */
export const ENTITY_QUALITY_THRESHOLDS: Record<LocalizationContentType, QualityThresholds> = {
  location: { 
    minNameLength: 2,         // "US", "UK" are valid country codes
    maxNameLength: 100,       // "British Indian Ocean Territory"
    // No short name constraints - locations don't use translated_primary_short
    minBodyLength: 100, 
    minTitleLength: 30,       // SEO title
    maxTitleLength: 60, 
    maxDescLength: 160,
    minExcerptLength: 50,     // Location summaries
    maxExcerptLength: 500,
  },
  topic: { 
    minNameLength: 3,         // "Tax" is a valid topic
    maxNameLength: 80,
    minShortNameLength: 3,    // Short topic names
    maxShortNameLength: 40,   // For nav/cards
    minBodyLength: 50, 
    minTitleLength: 20, 
    maxTitleLength: 60, 
    maxDescLength: 160,
    minExcerptLength: 30,
    maxExcerptLength: 500,
  },
  publication: { 
    minNameLength: 10,      // Publication titles need substance
    maxNameLength: 200,
    minBodyLength: 200, 
    minTitleLength: 30, 
    maxTitleLength: 60, 
    maxDescLength: 160,
    minExcerptLength: 50,   // Per memory: "500-700 chars for enriched excerpt teaser"
    maxExcerptLength: 700,
  },
  page: { 
    minNameLength: 5,       // Page names can be short like "About"
    maxNameLength: 150,
    minBodyLength: 200, 
    minTitleLength: 30, 
    maxTitleLength: 60, 
    maxDescLength: 160,
    minExcerptLength: 50,   // Per memory: "500-700 chars for enriched excerpt teaser"
    maxExcerptLength: 700,
  },
  route: { 
    minNameLength: 3,         // Route names like "FAQ" are valid
    maxNameLength: 80,
    minShortNameLength: 3,    // Short route tagline like "FAQ" or "Experts"
    maxShortNameLength: 60,   // Taglines for breadcrumbs (slightly longer than locations)
    minBodyLength: 30, 
    minTitleLength: 20, 
    maxTitleLength: 60, 
    maxDescLength: 160,
    minExcerptLength: 30,
    maxExcerptLength: 500,
  },
  profile: { 
    minNameLength: 2,       // Names like "Dr. J" are valid
    maxNameLength: 100,
    minBodyLength: 50, 
    minTitleLength: 20, 
    maxTitleLength: 70,     // RAISED 60→70 for profile names
    maxDescLength: 160,
    minExcerptLength: 30,
    maxExcerptLength: 500,
  },
  faq: { 
    minNameLength: 10,      // FAQ questions need context
    maxNameLength: 200,
    minBodyLength: 20, 
    minTitleLength: 10, 
    maxTitleLength: 120,    // FAQ questions for schema
    maxDescLength: 300,     // FAQ answers can be longer
    minExcerptLength: 20,
    maxExcerptLength: 300,
  },
  email_template: { 
    minNameLength: 5,
    maxNameLength: 100,
    minBodyLength: 10, 
    minTitleLength: 5, 
    maxTitleLength: 120, 
    maxDescLength: 0,
    minExcerptLength: 10,
    maxExcerptLength: 200,
  },
  comment: { 
    minNameLength: 0,       // Comments don't have names
    maxNameLength: 0,
    minBodyLength: 5, 
    minTitleLength: 0, 
    maxTitleLength: 0, 
    maxDescLength: 0,
    minExcerptLength: 0,
    maxExcerptLength: 0,
  },
  practice_area: { 
    minNameLength: 3,       // "Tax" is a valid practice area
    maxNameLength: 100,
    minBodyLength: 0, 
    minTitleLength: 5, 
    maxTitleLength: 100, 
    maxDescLength: 0,
    minExcerptLength: 30,
    maxExcerptLength: 500,
  },
  membership_plan: { 
    minNameLength: 3,
    maxNameLength: 100,
    minBodyLength: 10,      // JSON array of feature bullets
    minTitleLength: 0, 
    maxTitleLength: 0, 
    maxDescLength: 0,
    minExcerptLength: 0,
    maxExcerptLength: 0,
  },
  content_type: { 
    minNameLength: 3,
    maxNameLength: 60,
    minBodyLength: 0, 
    minTitleLength: 0, 
    maxTitleLength: 0, 
    maxDescLength: 0,
    minExcerptLength: 0,
    maxExcerptLength: 0,
  },
};
